import { Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { InputNumberFieldV2 as InputNumberField } from "../../../../../../atoms/FormElement/InputNumberField";
import { DecimalUtils, DISPLAY_SCALE, formatCurrency, formatDateByType } from "../../../../../../libs/utils";
import { useBookingSelector } from "../../../../hooks/useBookingSelector";
import type { ServiceCharge } from "../api";
import { GridHeaderCell } from "../GridHeaderCell";
import { gridTitleWithReset } from "../gridTitleWithReset";

const { Text } = Typography;

type FormRow = {
  amount: number;
  _vatRate: number;
};

type FormValues = {
  rows: FormRow[];
};

type ServiceChargeBreakdownProps = {
  rows: ServiceCharge[];
  totalServiceCharge?: number;
  onRowsChange?: (rows: ServiceCharge[]) => void;
};

const cellStyle: React.CSSProperties = { marginBottom: 0 };

export const ServiceChargeBreakdown = (props: ServiceChargeBreakdownProps) => {
  const { rows, totalServiceCharge, onRowsChange } = props;

  const { isPaymentPlanApprovalSent } = useBookingSelector();

  const lastSyncedRowsRef = useRef<string>("");
  const isResettingRowsRef = useRef(false);

  const defaultValues = useMemo(
    () => ({
      rows: rows.map((row) => ({
        amount: Number(row.amount),
        _vatRate: Number(row.amount) > 0 ? Number(row.vat) / Number(row.amount) : 0,
      })),
    }),
    [rows],
  );

  const methods = useForm<FormValues>({ defaultValues });
  const { reset, setValue, control } = methods;
  const watchedRows = useWatch({ control, name: "rows" });

  useEffect(() => {
    isResettingRowsRef.current = true;
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!onRowsChange || !watchedRows) return;
    if (isResettingRowsRef.current) {
      isResettingRowsRef.current = false;
      return;
    }

    const scheduleRowsFromForm = rows.map((sourceRow, rowIndex) => {
      const amountInput = watchedRows[rowIndex]?.amount ?? sourceRow.amount;
      const vatRate = watchedRows[rowIndex]?._vatRate ?? 0;
      const amount = DecimalUtils.toNumber(amountInput);
      const vat = DecimalUtils.toNumber(DecimalUtils.multiply(amountInput, vatRate));
      const total = DecimalUtils.toNumber(DecimalUtils.add([amountInput, DecimalUtils.multiply(amountInput, vatRate)]));

      return { ...sourceRow, amount, vat, total };
    });

    const serialized = JSON.stringify(scheduleRowsFromForm);
    if (lastSyncedRowsRef.current === serialized) return;
    lastSyncedRowsRef.current = serialized;

    onRowsChange(scheduleRowsFromForm);
  }, [watchedRows, rows, onRowsChange]);

  const resetAmounts = () => rows.forEach((row, idx) => setValue(`rows.${idx}.amount`, Number(row.amount)));

  const tableData = rows.map((row, idx) => ({ ...row, _idx: idx }));

  const amountInputs = watchedRows?.map((r) => r?.amount ?? 0) ?? [];
  const vatInputs = watchedRows?.map((r) => DecimalUtils.multiply(r?.amount ?? 0, r?._vatRate ?? 0)) ?? [];
  const totalAmountSum = DecimalUtils.add(amountInputs);
  const totalVatSum = DecimalUtils.add(vatInputs);
  const totalAmount = DecimalUtils.toNumber(totalAmountSum);
  const totalVat = DecimalUtils.toNumber(totalVatSum);
  const totalTotal = DecimalUtils.toNumber(DecimalUtils.add([totalAmountSum, totalVatSum]));

  const amountError =
    totalAmount != null &&
    totalServiceCharge != null &&
    !DecimalUtils.equals(totalAmount, totalServiceCharge, { scale: DISPLAY_SCALE })
      ? "Sum of service charges must equal the total service charge"
      : null;

  const columns: ColumnsType<(typeof tableData)[number]> = [
    {
      title: "INST #",
      dataIndex: "installment",
      key: "installment",
      width: 80,
    },
    {
      title: "FROM",
      dataIndex: "startDate",
      key: "startDate",
      width: 130,
      render: (value) => formatDateByType(value),
    },
    {
      title: "TO",
      dataIndex: "endDate",
      key: "endDate",
      width: 130,
      render: (value) => formatDateByType(value),
    },
    {
      title: "DUE DATE",
      dataIndex: "dueDate",
      key: "dueDate",
      width: 130,
      render: (value) => formatDateByType(value),
    },
    {
      title: gridTitleWithReset("SERVICE CHARGES", resetAmounts, isPaymentPlanApprovalSent),
      key: "amount",
      width: 180,
      render: (_, record) => (
        <InputNumberField
          fieldname={`rows.${record._idx}.amount`}
          control={control}
          format="currency"
          formItemProps={{ style: cellStyle }}
          inputNumberProps={{ placeholder: "0.00", min: 0 }}
          disabled={isPaymentPlanApprovalSent}
        />
      ),
    },
    {
      title: "VAT",
      key: "vat",
      align: "right",
      width: 110,
      render: (_, record) => {
        const row = watchedRows?.[record._idx];
        const vat = DecimalUtils.toNumber(DecimalUtils.multiply(row?.amount ?? 0, row?._vatRate ?? 0));
        return formatCurrency(vat, { symbol: false });
      },
    },
    {
      title: "TOTAL",
      key: "total",
      align: "right",
      width: 130,
      render: (_, record) => {
        const row = watchedRows?.[record._idx];
        const amountInput = row?.amount ?? 0;
        const vatInput = DecimalUtils.multiply(amountInput, row?._vatRate ?? 0);
        const total = DecimalUtils.toNumber(DecimalUtils.add([amountInput, vatInput]));
        return formatCurrency(total, { symbol: false });
      },
    },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <Text strong>Payment Schedule Breakdown</Text>
      </div>
      <FormProvider {...methods}>
        <Table
          dataSource={tableData}
          rowKey="_idx"
          columns={columns}
          pagination={false}
          size="small"
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={4}>
                <Text strong>Total</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">
                <Text strong style={{ color: amountError ? "var(--red-500, red)" : undefined }}>
                  {formatCurrency(totalAmount, { symbol: false })}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                <Text strong>{formatCurrency(totalVat, { symbol: false })}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                <Text strong>{formatCurrency(totalTotal, { symbol: false })}</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
          bordered
          components={{ header: { cell: GridHeaderCell } }}
        />
        {amountError && (
          <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
            {amountError}
          </Text>
        )}
      </FormProvider>
    </div>
  );
};
